import { defineStore } from 'pinia'
import { useAuth } from '@/store/auth'
import { user } from '@/libs/api'

let getUserRequest = null

/**
 * @typedef {object} UserPermissions
 * @property {boolean} canBeneficiaryCreate
 * @property {boolean} canBeneficiaryDelete
 * @property {boolean} canCardActivate
 * @property {boolean} canCardBlock
 * @property {boolean} canCardClose
 * @property {boolean} canCardCreate
 * @property {boolean} canCardWalletCreate
 * @property {boolean} canCardCvvView
 * @property {boolean} canCardNumberView
 * @property {boolean} canCardPasswordEdit
 * @property {boolean} canCardPasswordView
 * @property {boolean} canCardPinEdit
 * @property {boolean} canCardPinView
 * @property {boolean} canCardUnblock
 * @property {boolean} canCardView
 * @property {boolean} canCryptoBuy
 * @property {boolean} canCryptoSell
 * @property {boolean} canCurrencyExchangeCreate
 * @property {boolean} canPaymentCancel
 * @property {boolean} canPaymentCreate
 * @property {boolean} canPaymentDelete
 * @property {boolean} canPaymentDraftCreate
 * @property {boolean} canPaymentSign
 * @property {boolean} canUserDelete
 * @property {boolean} canUserEdit
 * @property {boolean} canWalletEdit
 * @property {boolean} canWalletTopUp
 * @property {boolean} canWalletTransfer
 * @property {boolean} canWalletClose
 * @property {boolean} canCardSmsBalanceNotificationEdit
 * @property {boolean} canDepositFromOpenBanking
 * @property {boolean} canCardOobConfirm
 */
/**
 * @typedef {object} UserProfile
 * @property {string} id
 * @property {string} email
 * @property {string} phone
 * @property {string} company
 * @property {string} username
 * @property {number} updatedAtMs
 */

export const useUser = defineStore('user', {
  state: () => ({
    /**
     * User license region (uk, lv)
     * @type {string|null}
     */
    region: null,
    /**
     * @type UserPermissions
     */
    permissions: {},
    /**
     * @type UserProfile
     */
    profile: {
      id: null,
      username: '',
      company: '',
    },
    /**
     * Default currency for the user. Used if no currency is selected.
     * @type string
     */
    defaultCurrencyId: 'EUR',
  }),
  getters: {
    authorized() {
      const auth = useAuth()
      return !!auth.refreshToken
    },

    /**
     * Check if user can make action
     * Ability to customize permissions
     *
     * @return {function(*): *}
     */
    can(state) {
      return (name) => {
        let perm = 'can' + name.charAt(0).toUpperCase() + name.slice(1)
        return state.permissions[perm] || false
      }
    },
  },
  actions: {
    getUser() {
      if (getUserRequest) {
        return getUserRequest
      }

      getUserRequest = user
        .getUser()
        .then(({ data }) => {
          this.region = data.data.region
          this.permissions = data.data.permissions
          this.profile = data.data.profile
        })
        .finally(() => {
          getUserRequest = null
        })

      return getUserRequest
    },
  },
})
